import { createAsyncThunk, createSlice, PayloadAction, Reducer } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';

import { useSelector } from 'react-redux';

import { RequestStatus } from 'src/interfaces/global';
import { RootState } from '../store';
import {
  AddContactsToCartPayload,
  AddFrameToCartPayload,
  OrderItem,
  WishlistState,
  Config,
  Wishlist,
  EditSavedItemsContactQuantityPayload,
  EditSavedItemsGlassesWarrantyPayload,
  GetWishlistPayload,
} from './wishlistInterfaces';
import wishlistServices from './wishlistServices';
import wishlistMock from '../../moks/wishlistMock.json';

const initialState: WishlistState = {
  config: null,
  wishlist: null,
  wishlistStatus: 'IDLE',
  formattedOrderItems: {},
  formattedOrderItemsStatus: 'IDLE',
  elementToWatchIDs: [],
  editSavedItemsContactQuantityStatus: 'IDLE',
  editSavedItemsGlassesWarrantyStatus: 'IDLE',
  sortValue: '1',
};

const SLICE_NAME = 'wishlist';

const makeWishListItemLoading = (wishlist: Wishlist, savedItemId: number) => {
  const wishlistCopy = cloneDeep(wishlist);
  const itemI = wishlistCopy.savedItems.findIndex(item => item.idList === savedItemId);
  wishlistCopy.savedItems[itemI] = { ...wishlistCopy.savedItems[itemI], isAddingToCart: true };
  return wishlistCopy;
};

function getProductsForAnalytics(state, payload) {
  let savedList = state.wishlist.wishlist.savedItems;
  let partNumber = payload.data['framePartNumber'];
  
  let selectedProduct = null;
  for (let i = 0; i < savedList.length; i++) {
    if (partNumber == savedList[i].catentries[0].partNumber) {
      selectedProduct = savedList[i];
    }
  }

  let prodUrl = selectedProduct.catentries[0].attributes.find(attr => attr.name === 'pageUrl')?.value;
  let prodCategory = selectedProduct.catentries[0].catEntryType.toLowerCase() === "eye" 
    ? "OPTICS" 
    : selectedProduct.catentries[0].catEntryType;

  var analyticsProduct = {};
  analyticsProduct[partNumber] = {
    Status: selectedProduct.catentries[0].available ? 'Available' : 'Out of Stock',
    Category: prodCategory,
    FrameType: 'STD',
    LensType: 'PLANO',
    LensUPC: '',
    Engraving: '',
    Price: Number(selectedProduct.totalPrice).toFixed(2),
    PriceFull: Number(selectedProduct.totalFinalPrice).toFixed(2),
    Units: selectedProduct.catentries[0].quantity + '',
    Sku: selectedProduct.catentries[0].attributes.find(attr => attr.name === 'name')?.value as string,
    ModelName: selectedProduct.catentries[0].attributes.find(attr => attr.name === 'name')?.value as string,
    Shape: '',
    FrameTechnology: '',
    LensTechnology: '',
    Brand: selectedProduct.catentries[0].attributes.find(attr => attr.name === 'brand')?.value as string,
    Image: selectedProduct.catentries[0].attributes.find(attr => attr.name === 'imageUrl')?.value as string,
    Url: prodUrl != null ? prodUrl as string : ''
  };

  return analyticsProduct;
}

export const getWishlistAsync = createAsyncThunk(
  `${SLICE_NAME}/getWishlist`,
  async (payload: GetWishlistPayload, thunkAPI) => {
    const config = (thunkAPI.getState() as RootState).wishlist.config;
    const response = await wishlistServices.getWishlist(config, payload);

    return { data: response.data.response, mergeItems: payload.mergeItems };
  }
);

export const addFrameToCartAsync = createAsyncThunk(
  `${SLICE_NAME}/addFrameToCart`,
  async (payload: AddFrameToCartPayload, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;

    thunkAPI.dispatch(
      setWishlist(makeWishListItemLoading(state.wishlist.wishlist, payload.savedItemId))
    );
    const deleteRes = await wishlistServices.deleteItem(
      state.wishlist.config,
      payload.savedItemId.toString()
    );

    if (deleteRes.data.response.success) {
      let analyticsProduct = getProductsForAnalytics(state, payload);
      window?.tealium_data2track?.push({
        Events_CartAdd: '1',
        id: 'Event',
        Products: analyticsProduct
      });
      await state.wishlist.config.actions.addToBagFrameLens(payload.data);
    }
  }
);

export const addContactsToCartAsync = createAsyncThunk(
  `${SLICE_NAME}/addContactsToCart`,
  async (payload: AddContactsToCartPayload, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;

    thunkAPI.dispatch(
      setWishlist(makeWishListItemLoading(state.wishlist.wishlist, payload.savedItemId))
    );
    const deleteRes = await wishlistServices.deleteItem(
      state.wishlist.config,
      payload.savedItemId.toString()
    );

    if (deleteRes.data.response.success) {
      await state.wishlist.config.actions.addToBagContacts(payload.data);

      window?.tealium_data2track?.push({
        tealium_event: 'AddToCart',
        id: 'Event',
        Events_CartAdd: '1',
      });
    }
  }
);

export const deleteItemAsync = createAsyncThunk(
  `${SLICE_NAME}/deleteItem`,
  async (payload: string, thunkAPI) => {
    const wishlist: WishlistState = (thunkAPI.getState() as RootState).wishlist;
    const response = await wishlistServices.deleteItem(wishlist.config, payload);
    if (response.data.response.success) {
      const wishlistLength = wishlist.wishlist.savedItems.length;

      thunkAPI.dispatch(getWishlistAsync({ pageNumber: 1, pageSize: wishlistLength, sortValue: wishlist.sortValue }));
      wishlist.config.actions.updateSavedItemsCount();
    }
    return response.data;
  }
);

export const editSavedItemsContactQuantityAsync = createAsyncThunk(
  `${SLICE_NAME}/editSavedItemsContactQuantity`,
  async (payload: EditSavedItemsContactQuantityPayload, thunkAPI) => {
    const config = (thunkAPI.getState() as RootState).wishlist.config;
    const response = await wishlistServices.editSavedItemsContactQuantity(config, payload);
    return response.data;
  }
);

export const editSavedItemsGlassesWarrantyAsync = createAsyncThunk(
  `${SLICE_NAME}/editSavedItemsGlassesWarranty`,
  async (payload: EditSavedItemsGlassesWarrantyPayload, thunkAPI) => {
    const wishlist: WishlistState = (thunkAPI.getState() as RootState).wishlist;
    const response = await wishlistServices.editSavedItemsGlassesWarranty(wishlist.config, payload);
    if (response.data.response.success) {
      const wishlistLength = wishlist.wishlist.savedItems.length;

      thunkAPI.dispatch(getWishlistAsync({ pageNumber: 1, pageSize: wishlistLength , sortValue: wishlist.sortValue}));
      wishlist.config.actions.updateSavedItemsCount();
    }
    return response.data;
  }
);

export const wishlistSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    saveConfig: (state, { payload }: PayloadAction<Config | null>) => {
      state.config = payload;
    },
    setWishlist: (state, { payload }: PayloadAction<Wishlist | null>) => {
      state.wishlist = payload;
    },
    setWishlistStatus: (state, { payload }: PayloadAction<RequestStatus>) => {
      state.wishlistStatus = payload;
    },
    setFormattedOrderItems: (state, { payload }: PayloadAction<{ [x: string]: OrderItem }>) => {
      state.formattedOrderItems = payload;
    },
    setFormattedOrderItemsStatus: (state, { payload }: PayloadAction<RequestStatus>) => {
      state.formattedOrderItemsStatus = payload;
    },
    setSortValue: (state, { payload }: PayloadAction<string>) => {
      state.sortValue = payload;
    }
  },
  extraReducers: builder => {
    builder
      // getWishlist
      .addCase(getWishlistAsync.pending, state => {
        state.wishlistStatus = 'LOADING';
      })
      .addCase(getWishlistAsync.fulfilled, (state, { payload }) => {
        state.wishlistStatus = 'SUCCESS';
        if (process.env.NODE_ENV !== 'development') {
          if (payload.mergeItems) {
            const newWishlist = cloneDeep(state.wishlist);
            newWishlist.savedItems = [...newWishlist.savedItems, ...payload.data.savedItems];
            state.wishlist = newWishlist;
            return;
          }
          state.wishlist = payload.data;
          return;
        }
        state.wishlist = wishlistMock;
      })
      .addCase(getWishlistAsync.rejected, (state, { error }) => {
        state.wishlistStatus = 'ERROR';
        console.error('>>> Error code:', error.code, 'error message:', error.message);
      })
      // editSavedItemsContactQuantityAsync
      .addCase(editSavedItemsContactQuantityAsync.pending, state => {
        state.editSavedItemsContactQuantityStatus = 'LOADING';
      })
      .addCase(editSavedItemsContactQuantityAsync.fulfilled, (state, { payload }) => {
        state.editSavedItemsContactQuantityStatus = 'SUCCESS';
      })
      .addCase(editSavedItemsContactQuantityAsync.rejected, (state, { error }) => {
        state.editSavedItemsContactQuantityStatus = 'ERROR';
        console.error('>>> Error code:', error.code, 'error message:', error.message);
      })
      // editSavedItemsGlassesWarrantyAsync
      .addCase(editSavedItemsGlassesWarrantyAsync.pending, state => {
        state.editSavedItemsGlassesWarrantyStatus = 'LOADING';
      })
      .addCase(editSavedItemsGlassesWarrantyAsync.fulfilled, (state, { payload }) => {
        state.editSavedItemsGlassesWarrantyStatus = 'SUCCESS';
      })
      .addCase(editSavedItemsGlassesWarrantyAsync.rejected, (state, { error }) => {
        state.editSavedItemsGlassesWarrantyStatus = 'ERROR';
        console.error('>>> Error code:', error.code, 'error message:', error.message);
      });
  },
});

export const {
  saveConfig,
  setWishlist,
  setWishlistStatus,
  setFormattedOrderItems,
  setFormattedOrderItemsStatus,
  setElementToWatchIDs,
  setSortValue,
} = wishlistSlice.actions;

export const selectConfig = (state: RootState): Config | null => state.wishlist.config;
export const selectWishlist = (state: RootState): Wishlist => state.wishlist.wishlist;
export const selectsortValue = (state: RootState): string => state.wishlist.sortValue;
export const selectWishlistStatus = (state: RootState): RequestStatus =>
  state.wishlist.wishlistStatus;
export const selectFormattedOrderItems = (state: RootState): { [x: string]: OrderItem } =>
  state.wishlist.formattedOrderItems;
export const selectFormattedOrderItemsStatus = (state: RootState): RequestStatus =>
  state.wishlist.formattedOrderItemsStatus;
export const selectElementToWatchIDs = (state: RootState): string[] =>
  state.wishlist.elementToWatchIDs;
export const selectEditSavedItemsContactQuantityStatus = (state: RootState): RequestStatus =>
  state.wishlist.editSavedItemsContactQuantityStatus;
export const selectEditSavedItemsGlassesWarrantyStatus = (state: RootState): RequestStatus =>
  state.wishlist.editSavedItemsGlassesWarrantyStatus;

export default wishlistSlice.reducer as Reducer<typeof initialState>;
